import { canIUse, Permission } from '@ali/hyper-api';
import { PermissionKeyType } from '@ali/hyper-api-permission';
import { deviceInfo } from '@ali/bara-uni-api';
import { useState, useEffect } from 'react';

// 权限结果枚举
// AUTHORIZED: 授权成功
// PARTIAL_AUTHORIZED: 部分授权
// DENIED: 授权拒绝，系统应用权限
// BIZ_DENIED: 业务授权拒绝，隐私合规管控，业务拒绝
// ROLLBACK: 隐私合规拒绝，安全中心开关开启直接关闭权限
// UNSUPPORTED: 权限不支持，通常指当前版本还未支持

const permissionList = [{
  id: 1,
  key: [PermissionKeyType.RECORD_AUDIO],
  name: '允许喵街访问麦克风权限',
  desc: '为了帮助您实现语音发送消息等功能',
  title: '麦克风',
  platform: []
}, {
  id: 2,
  key: [PermissionKeyType.READ_IMAGES, PermissionKeyType.WRITE_IMAGES],
  name: '允许喵街访问相册权限',
  desc: '为了实现您发帖、评价等上传图片和功能',
  title: '相册',
  platform: []
}, {
  id: 3,
  key: [PermissionKeyType.COARSE_LOCATION, PermissionKeyType.FINE_LOCATION],
  name: '允许喵街访问位置信息权限',
  desc: '为了给您提供周边商场的商品、优惠及服务',
  title: '位置信息',
  platform: []
}, {
  id: 4,
  key: [PermissionKeyType.TAKE_CAMERA],
  name: '允许喵街访问相机权限',
  desc: '为了帮你扫描二维码或商品和互动等功能',
  title: '相机',
  platform: []
}, {
  id: 5,
  key: [PermissionKeyType.READ_CALENDAR, PermissionKeyType.WRITE_CALENDAR],
  name: '允许喵街访问日历权限',
  desc: '为了在日历中添加领取权益、活动的日程提醒',
  title: '日历',
  platform: []
}, {
  id: 6,
  key: [PermissionKeyType.READ_CONTACTS, PermissionKeyType.WRITE_CONTACTS],
  name: '允许喵街访问通讯录权限',
  desc: '使您在购物时可以更便利地取用通讯录内的联系人信息，无需再手动输入',
  title: '通讯录',
  platform: ['android', 'harmony']
}, {
  id: 7,
  key: [PermissionKeyType.READ_TRACKING],
  name: '允许喵街访问广告标识符权限',
  desc: '为了更精准地给你推荐您可能想购买的商品',
  title: '广告标识符',
  platform: ['ios']
}];
export interface PermissionStatus {
  [key: string]: string;
}
export interface UsePermissionReturn {
  permissionList: typeof permissionList;
  checkPermissionType: (keyList: PermissionKeyType[], permissionsStatus: PermissionStatus) => string;
  setPermission: (platform: string[]) => boolean;
  checkPermission: (keyList: PermissionKeyType[]) => Promise<string>;
  isHarmony: boolean;
  permissionStatus: PermissionStatus;
  fetchAllPermissions: () => void;
}
export const usePermission = (): UsePermissionReturn => {
  const isHarmony = deviceInfo.OS.isHarmonyOS;
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>({});

  // 新增：批量拉取所有权限状态
  useEffect(() => {
    fetchAllPermissions();
  }, []); // 只在初始化时拉取

  const fetchAllPermissions = async () => {
    const statusObj: PermissionStatus = {};
    await Promise.all(permissionList.map(async item => {
      try {
        if (!item.key || item.key.length === 0) return;
        if (!setPermission(item.platform)) return;
        const status = await checkPermission(item.key);
        console.log(`fetchAllPermissions:  ${JSON.stringify(item.key)} ${status}`);
        statusObj[item.key.join(",")] = status;
      } catch (e) {
        console.log(`fetchAllPermissions error:  ${JSON.stringify(item.key)} ${JSON.stringify(e)}`);
        statusObj[item.key.join(",")] = '';
      }
    }));
    console.log(`fetchAllPermissions statusObj:  ${JSON.stringify(statusObj)}`);
    setPermissionStatus(statusObj);
  };
  const checkPermission = async (keyList: PermissionKeyType[]): Promise<string> => {
    if (canIUse(Permission.check)) {
      return new Promise(resolve => {
        Permission.check({
          permissions: keyList,
          privacyID: 'mjmt',
          onSuccess: res => {
            console.log(`Permission check success:  ${JSON.stringify(keyList)} ${JSON.stringify(res)}`);
            resolve(checkPermissionType(keyList, res));
          }
        });
      });
    } else {
      console.log('canIUse(Permission.check)不支持');
      return '权限设置';
    }
  };

  // 校验权限类型
  const checkPermissionType = (keyList: PermissionKeyType[], permissionsStatus: PermissionStatus) => {
    let status = '权限设置';
    if (keyList.every(key => permissionsStatus[key] === 'AUTHORIZED')) {
      status = '已允许';
    } else if (keyList.some(key => permissionsStatus[key] === 'AUTHORIZED')) {
      status = '部分允许';
    }
    return status;
  };

  // 三端显示校验
  const setPermission = (platform: string[]) => {
    if (platform.includes('android') && !deviceInfo.OS.isAndroid) {
      return false;
    }
    if (platform.includes('harmony') && !deviceInfo.OS.isHarmonyOS) {
      return false;
    }
    if (platform.includes('ios') && !deviceInfo.OS.isIOS) {
      return false;
    }
    return true;
  };
  return {
    permissionList,
    checkPermission,
    checkPermissionType,
    setPermission,
    isHarmony,
    permissionStatus,
    fetchAllPermissions
  };
};